import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react'
import { Bug, Paperclip, X } from 'lucide-react'
import { cn } from '../../lib/utils'

const MAX_SCREENSHOTS = 5
const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024
const ALLOWED_SCREENSHOT_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
])

interface BugReportDraft {
  title: string
  description: string
  reporterEmail: string
  attachScreenshots: boolean
  screenshots: File[]
}

interface FieldErrors {
  title?: string
  description?: string
  reporterEmail?: string
  screenshots?: string
}

export interface BugReportSubmission {
  title: string
  description: string
  reporterEmail?: string
  screenshots: File[]
}

export interface BugReportModalProps {
  isOpen: boolean
  isSubmitting?: boolean
  submitError?: string | null
  onClose: () => void
  onSubmit: (payload: BugReportSubmission) => Promise<void>
}

const initialDraft: BugReportDraft = {
  title: '',
  description: '',
  reporterEmail: '',
  attachScreenshots: false,
  screenshots: [],
}

function validateEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function validateDraft(draft: BugReportDraft): FieldErrors {
  const errors: FieldErrors = {}

  if (draft.title.trim().length < 4) {
    errors.title = 'Please add a short title.'
  }

  if (draft.description.trim().length < 12) {
    errors.description = 'Please include enough detail to reproduce the issue.'
  }

  if (draft.reporterEmail.trim().length > 0 && !validateEmail(draft.reporterEmail.trim())) {
    errors.reporterEmail = 'Please enter a valid email address.'
  }

  if (draft.attachScreenshots) {
    if (draft.screenshots.length > MAX_SCREENSHOTS) {
      errors.screenshots = `Attach up to ${MAX_SCREENSHOTS} screenshots.`
    } else {
      const oversized = draft.screenshots.find((file) => file.size > MAX_SCREENSHOT_BYTES)
      if (oversized) {
        errors.screenshots = 'Each screenshot must be 5MB or less.'
      } else {
        const unsupported = draft.screenshots.find((file) => !ALLOWED_SCREENSHOT_TYPES.has(file.type))
        if (unsupported) {
          errors.screenshots = 'Only PNG, JPG, WEBP, and GIF screenshots are supported.'
        }
      }
    }
  }

  return errors
}

export const BugReportModal = memo(function BugReportModal({
  isOpen,
  isSubmitting = false,
  submitError = null,
  onClose,
  onSubmit,
}: BugReportModalProps) {
  const [draft, setDraft] = useState<BugReportDraft>(initialDraft)
  const [errors, setErrors] = useState<FieldErrors>({})
  const titleRef = useRef<HTMLInputElement>(null)

  const resetForm = useCallback(() => {
    setDraft(initialDraft)
    setErrors({})
  }, [])

  const handleClose = useCallback(() => {
    if (isSubmitting) return
    resetForm()
    onClose()
  }, [isSubmitting, onClose, resetForm])

  useEffect(() => {
    if (!isOpen) return

    titleRef.current?.focus()

    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        handleClose()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => {
      document.body.style.overflow = originalOverflow
      window.removeEventListener('keydown', handleEscape)
    }
  }, [handleClose, isOpen])

  useEffect(() => {
    if (!isOpen) {
      resetForm()
    }
  }, [isOpen, resetForm])

  const handleSubmit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const validationErrors = validateDraft(draft)
    setErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) {
      return
    }

    await onSubmit({
      title: draft.title,
      description: draft.description,
      reporterEmail: draft.reporterEmail.trim() || undefined,
      screenshots: draft.attachScreenshots ? draft.screenshots : [],
    })
  }, [draft, onSubmit])

  const handleFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    setDraft((prev) => ({ ...prev, screenshots: files }))
  }, [])

  if (!isOpen) {
    return null
  }

  return (
    <div
      className="fixed inset-0 z-[100] overscroll-contain bg-black/50 px-4 py-6"
      onClick={handleClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bug-report-title"
        className={cn(
          'mx-auto mt-8 w-full max-w-2xl rounded-xl border border-border bg-card shadow-2xl',
          'max-h-[90vh] overflow-y-auto'
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-2">
            <Bug className="h-5 w-5 text-amber-500" aria-hidden="true" />
            <h2 id="bug-report-title" className="text-base font-semibold text-foreground">
              Report a Bug
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            aria-label="Close bug report form"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
          <div className="space-y-1.5">
            <label htmlFor="bug-title" className="text-sm font-medium text-foreground">
              Bug Title
            </label>
            <input
              ref={titleRef}
              id="bug-title"
              name="title"
              type="text"
              autoComplete="off"
              placeholder="Example: Submit button stays disabled…"
              value={draft.title}
              onChange={(event) => {
                setDraft((prev) => ({ ...prev, title: event.target.value }))
              }}
              className={cn(
                'w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground',
                'placeholder:text-muted-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                errors.title ? 'border-red-500' : 'border-border'
              )}
              aria-invalid={errors.title ? 'true' : 'false'}
            />
            {errors.title && (
              <p className="text-sm text-red-600" role="alert">
                {errors.title}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="bug-description" className="text-sm font-medium text-foreground">
              What Happened
            </label>
            <textarea
              id="bug-description"
              name="description"
              autoComplete="off"
              rows={5}
              placeholder="Describe what you expected, what happened instead, and steps to reproduce…"
              value={draft.description}
              onChange={(event) => {
                setDraft((prev) => ({ ...prev, description: event.target.value }))
              }}
              className={cn(
                'w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground',
                'placeholder:text-muted-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                errors.description ? 'border-red-500' : 'border-border'
              )}
              aria-invalid={errors.description ? 'true' : 'false'}
            />
            {errors.description && (
              <p className="text-sm text-red-600" role="alert">
                {errors.description}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="bug-reporter-email" className="text-sm font-medium text-foreground">
              Email for Follow-up (Optional)
            </label>
            <input
              id="bug-reporter-email"
              name="reporterEmail"
              type="email"
              inputMode="email"
              autoComplete="off"
              spellCheck={false}
              placeholder="name@example.com"
              value={draft.reporterEmail}
              onChange={(event) => {
                setDraft((prev) => ({ ...prev, reporterEmail: event.target.value }))
              }}
              className={cn(
                'w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground',
                'placeholder:text-muted-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                errors.reporterEmail ? 'border-red-500' : 'border-border'
              )}
              aria-invalid={errors.reporterEmail ? 'true' : 'false'}
            />
            {errors.reporterEmail && (
              <p className="text-sm text-red-600" role="alert">
                {errors.reporterEmail}
              </p>
            )}
          </div>

          <div className="rounded-lg border border-border bg-background/60 p-3">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                name="attachScreenshots"
                checked={draft.attachScreenshots}
                onChange={(event) => {
                  const checked = event.target.checked
                  setDraft((prev) => ({
                    ...prev,
                    attachScreenshots: checked,
                    screenshots: checked ? prev.screenshots : [],
                  }))
                }}
                className="mt-1 h-4 w-4 rounded border-border text-primary focus-visible:ring-2 focus-visible:ring-primary"
              />
              <span className="space-y-1">
                <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <Paperclip className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  Attach Screenshots
                </span>
                <span className="block text-xs text-muted-foreground">
                  Up to {MAX_SCREENSHOTS} images, 5MB each.
                </span>
              </span>
            </label>

            {draft.attachScreenshots && (
              <div className="mt-3 space-y-2">
                <label htmlFor="bug-screenshots" className="text-sm font-medium text-foreground">
                  Screenshots
                </label>
                <input
                  id="bug-screenshots"
                  name="screenshots"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  multiple
                  onChange={handleFileChange}
                  className="block w-full text-sm text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
                />
                {draft.screenshots.length > 0 && (
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    {draft.screenshots.map((file) => (
                      <li key={`${file.name}-${file.size}`} className="min-w-0 truncate">
                        {file.name} ({Math.max(1, Math.round(file.size / 1024))} KB)
                      </li>
                    ))}
                  </ul>
                )}
                {errors.screenshots && (
                  <p className="text-sm text-red-600" role="alert">
                    {errors.screenshots}
                  </p>
                )}
              </div>
            )}
          </div>

          {submitError && (
            <p className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" aria-live="polite">
              {submitError}
            </p>
          )}

          <div className="flex justify-end gap-3 border-t border-border pt-3">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting…' : 'Submit Bug Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
})
